import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthCallback - Handles Google OAuth redirect
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * This component:
 * 1. Extracts session_id from URL fragment (window.location.hash)
 * 2. Exchanges it for user data via backend
 * 3. Stores auth token and redirects to profile
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Use window.location.hash directly (more reliable than React Router's location)
        const hash = window.location.hash;
        console.log('🔐 AuthCallback: Processing hash:', hash);
        
        if (!hash || !hash.includes('session_id=')) {
          console.error('No session_id found in URL hash');
          setStatus('error');
          setErrorMessage('No authentication data found. Please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return;
        }

        // Extract session_id from hash
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('session_id parameter is empty');
          setStatus('error');
          setErrorMessage('Invalid authentication data. Please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return;
        }

        console.log('🔐 Processing Google OAuth session:', sessionId.substring(0, 20) + '...');
        setStatus('exchanging');

        // Exchange session_id for user data — hard 20s timeout so a backend hang
        // falls through to a retry UI instead of spinning forever.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);

        let response;
        try {
          response = await fetch(`${API}/api/auth/google/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include', // Important for cookies
            body: JSON.stringify({
              session_id: sessionId,
              accepted_terms: localStorage.getItem('scd_terms_accepted') === 'true',
            }),
            signal: controller.signal,
          });
        } catch (abortErr) {
          clearTimeout(timeoutId);
          if (abortErr.name === 'AbortError') {
            throw new Error('Sign-in is taking longer than expected. Please try again.');
          }
          throw abortErr;
        }
        clearTimeout(timeoutId);

        if (!response.ok) {
          const error = await response.json().catch(() => ({ detail: 'Authentication failed' }));
          throw new Error(error.detail || 'Authentication failed');
        }

        const data = await response.json();
        localStorage.removeItem('scd_terms_accepted');
        console.log('✅ Google OAuth successful:', data.user?.email);

        // Store the JWT token BEFORE the hard redirect, so AuthProvider picks it up
        // on reload and primes axios.defaults.headers.common['Authorization'].
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          setToken(data.access_token);
        }

        // Update user state and persist for the reload hop
        if (data.user) {
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
        }

        setStatus('success');

        // Clear the hash from URL so a refresh after redirect doesn't re-trigger
        // AuthCallback with a now-consumed session_id (which would 401).
        window.history.replaceState(null, '', window.location.pathname);

        // Small delay to show success state, then redirect
        setTimeout(() => {
          const redirectPath = data.is_new_user ? '/profile/setup' : '/profile';
          // Hard navigation ensures AuthProvider re-reads localStorage.token
          // and primes axios defaults cleanly for the first authenticated request.
          window.location.href = redirectPath;
        }, 500);

      } catch (error) {
        console.error('❌ Google OAuth failed:', error);
        setStatus('error');
        
        // Check if it's a session expiration error
        const isExpired = error.message?.includes('expired') || error.message?.includes('Invalid session');
        setErrorMessage(isExpired 
          ? 'Your sign-in session expired. Please try again.' 
          : (error.message || 'Sign in failed. Please try again.')
        );
        
        // Don't auto-redirect - let user click retry
      }
    };

    processAuth();
  }, [navigate, setUser, setToken]);

  const handleRetry = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Must redirect to /auth/callback (this route), NOT /profile — see Login button for reason.
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleBackToLogin = () => {
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="text-center p-8 max-w-md">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 border-4 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <p className="text-white text-xl font-medium">Completing sign in...</p>
            <p className="text-gray-400 text-sm mt-2">Please wait while we verify your credentials</p>
          </>
        )}
        
        {status === 'exchanging' && (
          <>
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <p className="text-white text-xl font-medium">Verifying with Google...</p>
            <p className="text-gray-400 text-sm mt-2">Almost there!</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-white text-xl font-medium">Sign in successful!</p>
            <p className="text-gray-400 text-sm mt-2">Redirecting you now...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-white text-xl font-medium">Sign in failed</p>
            <p className="text-red-400 text-sm mt-2 mb-6">{errorMessage}</p>
            
            <div className="flex flex-col gap-3">
              <button
                onClick={handleRetry}
                className="w-full px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl transition-colors"
              >
                Try Again with Google
              </button>
              <button
                onClick={handleBackToLogin}
                className="w-full px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
              >
                Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
